import { useDispatch, useSelector } from "react-redux";

import { useGetSettings } from "@/api/configuration/useConfigurationAPI";
import { CloseIcon, LayerIcon, PlusIcon } from "@/assets/icons";
import EmptyPlaceholder from "@/components/emptyPlaceholder";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { MAX_POI } from "@/constants/dataset";
import { useMap } from "@/hooks/map/core/useMapContext";
import { RootState } from "@/store";
import { savePolygonSettings } from "@/store/slice/spatialSlice";
import { enableDrawPolygon } from "@/store/slice/uiSlice";

import { twMerge } from "tailwind-merge";

import PolygonItem from "./polygonItem";

const TEST_ID_PREFIX = "custom-polygon-settings-popup";

interface PolygonSettingsPopupProps {
  onClose: (isSave?: boolean) => void;
}

export default function PolygonSettingsPopup(
  props: Readonly<PolygonSettingsPopupProps>
) {
  const { onClose } = props;

  const dispatch = useDispatch();

  const { enterDrawMode } = useMap();

  const { data } = useGetSettings();

  const { polygonSettingsTemp } = useSelector(
    (state: RootState) => state.spatial
  );

  const handleAddMorePolygon = () => {
    dispatch(enableDrawPolygon());
    enterDrawMode();
  };

  const handleSavePolygon = () => {
    dispatch(savePolygonSettings());
    onClose(true);
  };

  return (
    <div
      id="polygon-settings"
      className={twMerge(
        "z-[99] font-sans border-gainsboro bg-white rounded-lg w-[338px]",
        "flex flex-col py-4 absolute left-[370px] top-[30px]"
      )}
    >
      <div className="flex flex-col px-4 pb-4">
        <div className="flex flex-row justify-between items-center">
          <h1 className="text-txtHeading font-bold text-[20px]">
            Custom Polygon
          </h1>
          <button
            className="bg-transparent border-0 p-0"
            data-testid="btn-close-polygon"
            aria-label="Close"
            onClick={() => onClose()}
          >
            <CloseIcon className="fill-dim-gray" />
          </button>
        </div>
        <div>
          <p className="text-[10px] mt-2">
            List of polygon{" "}
            <span className="text-teal-500" data-testid="polygon-count">
              {Object.keys(polygonSettingsTemp ?? {}).length || 0}/
              <b>{data?.max_catchments || MAX_POI}</b>
            </span>
          </p>

          <div className="flex flex-col mt-3 space-y-3">
            <div className="flex flex-col space-y-3 max-h-[258px] overflow-y-auto">
              {polygonSettingsTemp &&
              Object.keys(polygonSettingsTemp ?? {}).length > 0 ? (
                Object.keys(polygonSettingsTemp ?? {}).map((key) => (
                  <PolygonItem key={key} polygon={polygonSettingsTemp[key]} />
                ))
              ) : (
                <EmptyPlaceholder
                  className="my-3 bg-white px-4"
                  icon={
                    <LayerIcon
                      className="fill-light-gray mb-2.5"
                      width={21}
                      height={24}
                    />
                  }
                  description="You haven't saved any polygons. Create and save a custom polygon to reuse it later."
                />
              )}
            </div>

            <Button
              variant="ghost"
              type="button"
              className="mx-auto w-full mt-0 disabled:bg-transparent group"
              onClick={handleAddMorePolygon}
              disabled={
                Object.keys(polygonSettingsTemp ?? {}).length ===
                (data?.max_catchments || 100)
              }
              data-testid={`${TEST_ID_PREFIX}-add-more-button`}
            >
              <PlusIcon
                width={12}
                className="fill-dim-gray group-disabled:fill-dim-gray/30"
              />
              <span className="text-sm font-semibold text-dim-gray group-disabled:text-dim-gray/30">
                Add More
              </span>
            </Button>
          </div>
        </div>
      </div>

      <Separator className="h-2 bg-white-smoke" />

      <div className="flex justify-center pt-4 px-4">
        <Button
          type="button"
          onClick={handleSavePolygon}
          variant="default"
          className="w-full font-semibold text-sm text-white"
          data-testid={`${TEST_ID_PREFIX}-save-button`}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
